<template>
    <v-container class="flex flex-col items-start justify-start space-y-lg">
        <div class="mx-auto flex flex-col items-center justify-center space-y-lg lg:w-[900px]">
            <v-h3 tag="h1">
                {{ blokData.title }}
            </v-h3>

            <nuxt-picture v-if="blokData.image"
                :src="blokData.image.filename"
                :alt="blokData.image.alt ?? blokData.title"
                provider="storyblok"
                loading="lazy"
                class="-mx-xs block md:mx-auto"
                sizes="xs:100vw sm:640px md:768px lg:1024px xl:1280px 2xl:1536px" />

            <div class="prose prose-sm mx-sm lg:prose-lg xl:prose-xl md:mx-auto">
                <sb-rich-text :document="blokData.content" />
            </div>
        </div>
    </v-container>

    <sb-article-recommendations v-if="showRecommendations"
        :content="recommendationsContent"
        :story="story" />

    <div class="my-2xl bg-gray-50 px-sm md:px-0">
        <v-container class="space-y-lg py-lg">
            <v-h3>{{ t('read-also') }}</v-h3>

            <sb-article-feed-lite :feed="blokData.category"
                :story="story" />

            <div class="flex justify-center">
                <v-button theme="black"
                    size="base"
                    type="submit"
                    class="text-center"
                    :aria-label="t('read-more')"
                    :to="`/${$t('storyblok.articles')}`">
                    {{ t('read-more') }}
                </v-button>
            </div>
        </v-container>
    </div>
</template>

<script setup lang="ts">
import type { ISbStoryData } from 'storyblok-js-client';
import { type ProductRecommendationsContent } from '~~/types';

const properties = defineProps<{
    story: ISbStoryData
}>();

const blokData = computed(() => properties.story.content);

const { t } = useI18n();

const recommendations = useStoryblokStory('global/article-product-recommendations');

// eslint-disable-next-line max-len
const recommendationsContent = computed(() => recommendations.value?.content) as ComputedRef<ProductRecommendationsContent>;

const currentCategories = computed(() => blokData.value.category[0] || '');

const showRecommendations = computed<boolean>(() => {
    // eslint-disable-next-line max-len
    if (recommendationsContent.value?.article_categories.includes(currentCategories.value)) {
        return true;
    }
    return false;
});

useSeoMeta({
    title: blokData.value.title || properties.story.name,
    // eslint-disable-next-line max-len
    description: blokData.value.ingress?.content?.[0]?.content?.[0]?.text ?? undefined,
    ogTitle: blokData.value.title || properties.story.name,
    // eslint-disable-next-line max-len
    ogDescription: blokData.value.ingress?.content?.[0]?.content?.[0]?.text ?? undefined,
    ogImage: blokData.value.image?.filename ?? undefined,
});
</script>

<i18n lang="json">
{
    "et": {
        "read-also": "Loe ka lisaks",
        "read-more": "Loe rohkem Masku blogist"
    },
    "fi": {
        "read-also": "Lue myös",
        "read-more": "Lue lisää Maskun blogista"
    }
}
</i18n>
